import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BarChart3, Eye, Loader2, Pencil, Plus, Save, Trash2, X } from 'lucide-react'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useUser } from '@/shared/hooks/useUser'
import { useAccessibleBots, useBots } from '@/shared/hooks/useBots'
import {
  useCreateShowCampaign,
  useDeleteShowCampaign,
  useShowCampaigns,
  useUpdateShowCampaign,
} from '@/shared/hooks/useShows'
import type { Bot, ShowButton, ShowCampaign, ShowKind } from '@/shared/types'

const placeholders = [
  '$first_name',
  '$username',
  '$last_name',
  '$first_name_link',
  '$username_link',
  '$last_name_link',
]

interface FormState {
  id?: string
  kind: ShowKind
  title: string
  text_template: string
  enabled: boolean
  max_impressions: string
  starts_at: string
  ends_at: string
  bot_ids: string[]
  buttons: ShowButton[]
}

const emptyForm = (): FormState => ({
  kind: 'welcome',
  title: '',
  text_template: '',
  enabled: true,
  max_impressions: '',
  starts_at: '',
  ends_at: '',
  bot_ids: [],
  buttons: [],
})

export default function Shows() {
  const navigate = useNavigate()
  const { telegramId, hapticFeedback, hapticNotification } = useTelegram()
  const { data: user, isLoading: userLoading } = useUser(telegramId)
  const { data: ownedBots = [], isLoading: botsLoading } = useBots(user?.id)
  const { data: accessibleBots = [], isLoading: accessibleBotsLoading } = useAccessibleBots(!!user)
  const { data: campaigns = [], isLoading: campaignsLoading } = useShowCampaigns()
  const createCampaign = useCreateShowCampaign()
  const updateCampaign = useUpdateShowCampaign()
  const deleteCampaign = useDeleteShowCampaign()
  const [form, setForm] = useState<FormState | null>(null)

  const bots = useMemo(() => {
    const seen = new Set<string>()
    const combined: Bot[] = []
    for (const bot of [...ownedBots, ...accessibleBots]) {
      if (seen.has(bot.id)) continue
      seen.add(bot.id)
      combined.push(bot)
    }
    return combined
  }, [ownedBots, accessibleBots])
  const botsById = useMemo(() => new Map(bots.map((bot) => [bot.id, bot])), [bots])
  const accessibleBotIds = useMemo(() => new Set(bots.map((bot) => bot.id)), [bots])
  const isLoading = userLoading || botsLoading || accessibleBotsLoading || campaignsLoading
  const isSaving = createCampaign.isPending || updateCampaign.isPending

  const startCreate = () => {
    hapticFeedback('light')
    setForm(emptyForm())
  }

  const startEdit = (campaign: ShowCampaign) => {
    hapticFeedback('light')
    setForm({
      id: campaign.id,
      kind: campaign.kind,
      title: campaign.title,
      text_template: campaign.text_template,
      enabled: campaign.enabled,
      max_impressions: campaign.max_impressions?.toString() || '',
      starts_at: toLocalDateTime(campaign.starts_at),
      ends_at: toLocalDateTime(campaign.ends_at),
      bot_ids: campaign.bot_ids || [],
      buttons: campaign.buttons || [],
    })
  }

  const submit = async () => {
    if (!form || isSaving) return
    if (!form.title.trim() || !form.text_template.trim() || form.bot_ids.length === 0) {
      hapticNotification?.('error')
      window.Telegram?.WebApp?.showAlert?.('Заполните название, текст и выберите хотя бы одного бота')
      return
    }

    const parsedMaxImpressions = Number(form.max_impressions)
    const payload = {
      kind: form.kind,
      title: form.title.trim(),
      text_template: form.text_template,
      parse_mode: 'HTML',
      enabled: form.enabled,
      max_impressions: form.max_impressions && parsedMaxImpressions > 0 ? parsedMaxImpressions : null,
      starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      bot_ids: form.bot_ids,
      buttons: form.buttons.filter((button) => button.text.trim() && (button.url || button.callback_data)),
    }

    try {
      if (form.id) {
        await updateCampaign.mutateAsync({ id: form.id, data: payload })
      } else {
        await createCampaign.mutateAsync(payload)
      }
      hapticNotification?.('success')
      setForm(null)
    } catch (error) {
      console.error('Failed to save show campaign:', error)
      hapticNotification?.('error')
    }
  }

  const removeCampaign = async (campaign: ShowCampaign) => {
    hapticFeedback('medium')
    try {
      await deleteCampaign.mutateAsync(campaign.id)
      hapticNotification?.('success')
      if (form?.id === campaign.id) setForm(null)
    } catch (error) {
      console.error('Failed to delete show campaign:', error)
      hapticNotification?.('error')
    }
  }

  return (
    <div className="min-h-screen bg-tg-bg">
      <div className="p-4 pb-28 pt-[96px] max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-tg-text">Показы</h1>
            <p className="text-sm text-tg-hint mt-1">Приветственные и обычные посты для ботов</p>
          </div>
          <button
            onClick={startCreate}
            className="flex items-center justify-center w-12 h-12 bg-tg-button text-tg-button-text rounded-xl active:scale-95 transition-all"
            aria-label="Создать показ"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex min-h-[320px] items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
          </div>
        ) : (
          <div className="space-y-4">
            {form && (
              <ShowForm
                form={form}
                bots={bots}
                isSaving={isSaving}
                onChange={setForm}
                onCancel={() => setForm(null)}
                onSubmit={submit}
              />
            )}

            {campaigns.length === 0 && !form ? (
              <div className="py-16 text-center">
                <Eye className="w-12 h-12 text-tg-hint mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-tg-text mb-2">Показов пока нет</h2>
                <p className="text-sm text-tg-hint mb-6">Создайте приветственный или обычный пост и привяжите его к ботам</p>
                <button
                  onClick={startCreate}
                  className="inline-flex items-center gap-2 px-4 py-3 bg-tg-button text-tg-button-text rounded-xl"
                >
                  <Plus className="w-5 h-5" />
                  Создать
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {campaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    botsById={botsById}
                    onOpenStats={() => {
                      hapticFeedback('light')
                      navigate(`/shows/${campaign.id}/statistics`)
                    }}
                    canManage={
                      campaign.owner_id === user?.id ||
                      ((campaign.bot_ids?.length || 0) > 0 && campaign.bot_ids.every((id) => accessibleBotIds.has(id)))
                    }
                    onEdit={() => startEdit(campaign)}
                    onDelete={() => removeCampaign(campaign)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function ShowForm({
  form,
  bots,
  isSaving,
  onChange,
  onCancel,
  onSubmit,
}: {
  form: FormState
  bots: Bot[]
  isSaving: boolean
  onChange: (form: FormState) => void
  onCancel: () => void
  onSubmit: () => void
}) {
  const update = (patch: Partial<FormState>) => onChange({ ...form, ...patch })

  const toggleBot = (botId: string) => {
    const next = form.bot_ids.includes(botId)
      ? form.bot_ids.filter((id) => id !== botId)
      : [...form.bot_ids, botId]
    update({ bot_ids: next })
  }

  const insertPlaceholder = (placeholder: string) => {
    update({ text_template: `${form.text_template}${form.text_template ? ' ' : ''}${placeholder}` })
  }

  return (
    <div className="rounded-lg border border-transparent bg-tg-secondary-bg p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex rounded-lg bg-tg-bg p-1">
          {(['welcome', 'regular'] as ShowKind[]).map((kind) => (
            <button
              key={kind}
              onClick={() => update({ kind })}
              className={`px-3 py-2 text-sm rounded-md transition-colors ${
                form.kind === kind ? 'bg-tg-button text-tg-button-text' : 'text-tg-hint'
              }`}
            >
              {kind === 'welcome' ? 'Привет' : 'Обычный'}
            </button>
          ))}
        </div>
        <button onClick={onCancel} className="p-2 text-tg-hint" aria-label="Закрыть">
          <X className="w-5 h-5" />
        </button>
      </div>

      <label className="block">
        <span className="text-xs text-tg-hint">Название</span>
        <input
          value={form.title}
          onChange={(event) => update({ title: event.target.value })}
          className="mt-1 w-full rounded-lg border border-transparent bg-tg-bg px-3 py-3 text-tg-text outline-none focus:border-tg-button"
          placeholder="Например: привет для новых пользователей"
        />
      </label>

      <label className="block">
        <span className="text-xs text-tg-hint">Текст сообщения</span>
        <textarea
          value={form.text_template}
          onChange={(event) => update({ text_template: event.target.value })}
          className="mt-1 min-h-[140px] w-full rounded-lg border border-transparent bg-tg-bg px-3 py-3 text-tg-text outline-none focus:border-tg-button"
          placeholder="Приветствуем $first_name_link, рады видеть вас"
        />
      </label>

      <div className="flex flex-wrap gap-2">
        {placeholders.map((placeholder) => (
          <button
            key={placeholder}
            onClick={() => insertPlaceholder(placeholder)}
            className="px-2.5 py-1.5 rounded-md bg-tg-bg text-xs text-tg-button border border-transparent"
          >
            {placeholder}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs text-tg-hint">Лимит показов</span>
          <input
            value={form.max_impressions}
            type="number"
            min="1"
            onChange={(event) => update({ max_impressions: event.target.value })}
            className="mt-1 w-full rounded-lg border border-transparent bg-tg-bg px-3 py-3 text-tg-text outline-none focus:border-tg-button"
            placeholder="Без лимита"
          />
        </label>
        <label className="block">
          <span className="text-xs text-tg-hint">Начало</span>
          <input
            value={form.starts_at}
            type="datetime-local"
            onChange={(event) => update({ starts_at: event.target.value })}
            className="mt-1 w-full rounded-lg border border-transparent bg-tg-bg px-3 py-3 text-tg-text outline-none focus:border-tg-button"
          />
        </label>
        <label className="block">
          <span className="text-xs text-tg-hint">Конец</span>
          <input
            value={form.ends_at}
            type="datetime-local"
            onChange={(event) => update({ ends_at: event.target.value })}
            className="mt-1 w-full rounded-lg border border-transparent bg-tg-bg px-3 py-3 text-tg-text outline-none focus:border-tg-button"
          />
        </label>
      </div>

      <div>
        <div className="text-xs text-tg-hint mb-2">Боты</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {bots.map((bot) => (
            <button
              key={bot.id}
              onClick={() => toggleBot(bot.id)}
              className={`text-left rounded-lg border px-3 py-3 transition-colors ${
                form.bot_ids.includes(bot.id)
                  ? 'border-tg-button bg-tg-button/10 text-tg-text'
                  : 'border-transparent bg-tg-bg text-tg-hint'
              }`}
            >
              <div className="font-medium truncate">{bot.name}</div>
              <div className="text-xs truncate">@{bot.username}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-tg-text">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => update({ enabled: event.target.checked })}
            className="w-4 h-4 accent-tg-button"
          />
          Активен
        </label>
        <button
          onClick={() => update({ buttons: [...form.buttons, { text: '', url: '', row_index: form.buttons.length }] })}
          className="ml-auto px-3 py-2 rounded-lg border border-transparent bg-tg-bg text-sm text-tg-text"
        >
          Кнопка
        </button>
      </div>

      {form.buttons.length > 0 && (
        <div className="space-y-2">
          {form.buttons.map((button, index) => (
            <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2">
              <input
                value={button.text}
                onChange={(event) => {
                  const buttons = [...form.buttons]
                  buttons[index] = { ...button, text: event.target.value }
                  update({ buttons })
                }}
                className="rounded-lg border border-transparent bg-tg-bg px-3 py-2 text-sm text-tg-text outline-none"
                placeholder="Текст"
              />
              <input
                value={button.url || ''}
                onChange={(event) => {
                  const buttons = [...form.buttons]
                  buttons[index] = { ...button, url: event.target.value }
                  update({ buttons })
                }}
                className="rounded-lg border border-transparent bg-tg-bg px-3 py-2 text-sm text-tg-text outline-none"
                placeholder="https://..."
              />
              <button
                onClick={() => update({ buttons: form.buttons.filter((_, i) => i !== index) })}
                className="p-2 text-tg-hint"
                aria-label="Удалить кнопку"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onSubmit}
        disabled={isSaving}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-tg-button px-4 py-3 font-medium text-tg-button-text disabled:opacity-60"
      >
        {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
        Сохранить
      </button>
    </div>
  )
}

function CampaignCard({
  campaign,
  botsById,
  onOpenStats,
  canManage,
  onEdit,
  onDelete,
}: {
  campaign: ShowCampaign
  botsById: Map<string, Bot>
  onOpenStats: () => void
  canManage: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  const botNames = campaign.bot_ids
    ?.map((id) => botsById.get(id)?.name)
    .filter(Boolean)
    .join(', ')

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpenStats}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpenStats()
        }
      }}
      className="rounded-lg border border-transparent bg-tg-secondary-bg p-4 text-left active:scale-[0.99] transition-transform"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full ${campaign.enabled ? 'bg-green-500' : 'bg-tg-hint'}`} />
            <span className="text-xs uppercase text-tg-hint">{campaign.kind === 'welcome' ? 'Привет' : 'Обычный'}</span>
          </div>
          <h2 className="font-semibold text-tg-text truncate">{campaign.title}</h2>
          <p className="text-sm text-tg-hint truncate mt-1">{botNames || 'Боты не найдены'}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={(event) => {
              event.stopPropagation()
              onOpenStats()
            }}
            className="p-2 text-tg-button"
            aria-label="Статистика"
          >
            <BarChart3 className="w-5 h-5" />
          </button>
          {canManage && (
            <>
              <button
                onClick={(event) => {
                  event.stopPropagation()
                  onEdit()
                }}
                className="p-2 text-tg-hint"
                aria-label="Редактировать показ"
              >
                <Pencil className="w-5 h-5" />
              </button>
              <button
                onClick={(event) => {
                  event.stopPropagation()
                  onDelete()
                }}
                className="p-2 text-tg-hint"
                aria-label="Удалить показ"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <Metric label="Отправлено" value={campaign.sent_count} />
        <Metric label="Ошибки" value={campaign.failed_count} />
        <Metric label="Резерв" value={campaign.reserved_count} />
      </div>
      {campaign.max_impressions != null && (
        <div className="mt-3 h-1.5 bg-tg-bg rounded-full overflow-hidden">
          <div
            className="h-full bg-tg-button"
            style={{ width: `${Math.min(100, (campaign.reserved_count / Math.max(campaign.max_impressions, 1)) * 100)}%` }}
          />
        </div>
      )}
    </div>
  )
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-tg-bg px-3 py-2">
      <div className="text-lg font-semibold text-tg-text">{value}</div>
      <div className="text-[11px] text-tg-hint">{label}</div>
    </div>
  )
}

function toLocalDateTime(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  const offset = date.getTimezoneOffset()
  const local = new Date(date.getTime() - offset * 60 * 1000)
  return local.toISOString().slice(0, 16)
}
